import React from 'react';

import { Link } from '~/lib/react-router-dom';

import ArticleIcon from '@mui/icons-material/Article';
import ChecklistIcon from '@mui/icons-material/Checklist';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DescriptionIcon from '@mui/icons-material/Description';
import EventNoteIcon from '@mui/icons-material/EventNote';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ForumIcon from '@mui/icons-material/Forum';
import LinkIcon from '@mui/icons-material/Link';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PaymentsIcon from '@mui/icons-material/Payments';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import { Box, Button, IconButton, Link as MuiLink, Paper, Stack, Tooltip, Typography } from '@mui/material';

import featureToggle from '~/utils/featureToggle';
import isProd from '~/utils/isProd';

import { accountPath } from './Account/accountRelatedModel';

const FEED_PATH_SUFFIX = '/feed';

const ACCOUNT_ITEMS = [
  { label: 'Related', icon: LinkIcon, pathSuffix: '' },
  { label: 'Feed', icon: ForumIcon, pathSuffix: FEED_PATH_SUFFIX },
];

export const PRICING_TOOL_PATH = 'hotel-proposal-pricing-tool';

const WORKFLOW_ITEMS = [
  { label: 'Hotel Proposal Pricing Tool', icon: PaymentsIcon, pathSuffix: `/${PRICING_TOOL_PATH}` },
  { label: 'Assets', icon: FolderOpenIcon, pathSuffix: '/assets' },
  { label: 'Deal Information', icon: DescriptionIcon, pathSuffix: '/deal-information' },
  { label: 'CM Checklist', icon: ChecklistIcon, pathSuffix: '/cm-checklist' },
  { label: 'Rates and Inventory', icon: EventNoteIcon, pathSuffix: '/rates-and-inventory' },
  { label: 'Content', icon: ArticleIcon, pathSuffix: '/content' },
  ...(isProd() ? [] : [{ label: 'Project Management', icon: ViewKanbanIcon, pathSuffix: '/project-management' }]),
  { label: 'Feed', icon: ForumIcon, pathSuffix: FEED_PATH_SUFFIX },
];

function withoutHiddenFeed<T extends { pathSuffix: string }>(items: Array<T>) {
  return featureToggle.availableToShow('CHATTER_FEED_ENABLED')
    ? items
    : items.filter((item) => item.pathSuffix !== FEED_PATH_SUFFIX);
}

interface Props {
  accountId?: string;
  activeItem?: string;
  collapsed: boolean;
  hotelCode?: string;
  hotelName: string;
  isPropertyMissing?: boolean;
  location?: string;
  offerId?: string;
  propertyId?: string;
  onLockedClick?: () => void;
  onToggleCollapsed: () => void;
}

export default function OfferBuilderSidebar({
  accountId,
  activeItem = 'Content',
  collapsed,
  hotelCode,
  hotelName,
  isPropertyMissing = false,
  location,
  offerId,
  propertyId,
  onLockedClick,
  onToggleCollapsed,
}: Props) {
  const pageLinks = [
    ...(accountId ? [{ label: 'Vendor', icon: StorefrontIcon, path: `/vendors/${accountId}` }] : []),
    ...(offerId ? [{ label: 'Offer', icon: LocalOfferIcon, path: `/offers/${offerId}` }] : []),
  ];
  const navGroups = [
    ...(accountId && featureToggle.availableToShow('ACCOUNT_VIEW_ENABLED')
      ? [
          {
            title: 'Account',
            getPath: (suffix: string) => accountPath(accountId, offerId, suffix),
            items: withoutHiddenFeed(ACCOUNT_ITEMS),
            isGroupLockable: false,
          },
        ]
      : []),
    ...(offerId
      ? [
          {
            title: 'Hotel proposal',
            getPath: (suffix: string) => `/offers/${offerId}/offer-builder${suffix}`,
            items: withoutHiddenFeed(WORKFLOW_ITEMS),
            isGroupLockable: true,
          },
        ]
      : []),
  ];

  return (
    <Paper
      square
      variant="outlined"
      sx={{
        display: { xs: 'none', lg: 'block' },
        minHeight: '100vh',
        borderTop: 0,
        borderBottom: 0,
        borderLeft: 0,
        px: collapsed ? 1 : 3,
        py: 2.5,
      }}
    >
      <Stack direction={collapsed ? 'column' : 'row'} alignItems="center" justifyContent="flex-end" spacing={1}>
        {collapsed &&
          pageLinks.map(({ label, icon: Icon, path }) => (
            <Tooltip key={label} title={label} placement="right">
              <IconButton size="small" color="primary" component={Link} to={path} aria-label={label}>
                <Icon fontSize="small" />
              </IconButton>
            </Tooltip>
          ))}
        <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
          <IconButton size="small" onClick={onToggleCollapsed} aria-label="Toggle sidebar">
            {collapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Stack>

      {!collapsed && (
        <Box sx={{ pb: 2, mb: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {accountId && propertyId ? (
              <MuiLink component={Link} to={`/vendors/${accountId}/properties/${propertyId}`} underline="hover">
                {hotelName}
              </MuiLink>
            ) : (
              hotelName
            )}
          </Typography>
          {location && (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary', mt: 1 }}>
              <LocationOnIcon fontSize="small" />
              <Typography variant="body2">{location}</Typography>
            </Stack>
          )}
          {hotelCode && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>
              {hotelCode}
            </Typography>
          )}
          {pageLinks.length > 0 && (
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              {pageLinks.map(({ label, icon: Icon, path }) => (
                <Button
                  key={label}
                  component={Link}
                  to={path}
                  size="small"
                  variant="outlined"
                  startIcon={<Icon />}
                  fullWidth
                  sx={{ textTransform: 'none' }}
                >
                  {label}
                </Button>
              ))}
            </Stack>
          )}
        </Box>
      )}

      {navGroups.map(({ title, getPath, items, isGroupLockable }) => (
        <Box key={title} sx={{ mb: 2 }}>
          {!collapsed && (
            <Typography variant="overline" color="text.secondary">
              {title}
            </Typography>
          )}
          <Stack component="nav" spacing={0.5} sx={{ mt: 1 }}>
            {items.map(({ label, icon: Icon, pathSuffix }) => {
              const isLocked = isPropertyMissing && isGroupLockable && pathSuffix !== `/${PRICING_TOOL_PATH}`;
              const linkProps =
                isLocked && onLockedClick
                  ? { onClick: onLockedClick }
                  : { component: Link, to: getPath(pathSuffix), disabled: isLocked };
              const button = (
                <Button
                  key={label}
                  {...linkProps}
                  startIcon={collapsed ? undefined : <Icon />}
                  variant={activeItem === label ? 'contained' : 'text'}
                  color="primary"
                  fullWidth
                  sx={{
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    minWidth: 0,
                    px: collapsed ? 1 : 2,
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {collapsed ? <Icon fontSize="small" /> : label}
                </Button>
              );

              if (!collapsed) {
                return button;
              }

              return (
                <Tooltip key={label} title={label} placement="right">
                  <span>{button}</span>
                </Tooltip>
              );
            })}
          </Stack>
        </Box>
      ))}
    </Paper>
  );
}
